import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getAllLocations, brandInfo } from '../data/locations';
import { schoolInfo } from '../data/mock';
import { Menu, X, Globe, ChevronDown, MapPin } from 'lucide-react';

const Header = ({ currentLocation = null }) => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLocationsOpen, setIsLocationsOpen] = useState(false);
  const locationsRef = useRef(null);
  const routerLocation = useLocation();
  const locations = getAllLocations();
  
  // Check if we're on the main Surabaya page or a location subpage
  const isMainPage = routerLocation.pathname === '/' || routerLocation.pathname === '/gallery';
  const isLocationPage = routerLocation.pathname.startsWith('/standorte/');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationsRef.current && !locationsRef.current.contains(event.target)) {
        setIsLocationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Use appropriate logo and name
  const logoUrl = isMainPage ? schoolInfo.logoUrl : (currentLocation?.schoolInfo?.logoUrl || brandInfo.logoUrl);
  const displayName = isMainPage ? schoolInfo.name : brandInfo.name;

  // Navigation items for main Surabaya page
  const mainPageNavItems = [
    { labelEn: 'About', labelId: 'Tentang', href: '#about' },
    { labelEn: 'Classes', labelId: 'Kelas', href: '#classes' },
    { labelEn: 'Instructor', labelId: 'Instruktur', href: '#instructor' },
    { labelEn: 'Gallery', labelId: 'Galeri', href: '#gallery' },
    { labelEn: 'Contact', labelId: 'Kontak', href: '#contact' },
  ];

  // Navigation items for location pages
  const locationPageNavItems = [
    { labelEn: 'About', labelId: 'Tentang', href: '#about' },
    { labelEn: 'Classes', labelId: 'Kelas', href: '#classes' },
    { labelEn: 'Instructor', labelId: 'Instruktur', href: '#instructor' },
    ...(currentLocation?.hasGallery ? [{ labelEn: 'Gallery', labelId: 'Galeri', href: '#gallery' }] : []),
    { labelEn: 'Contact', labelId: 'Kontak', href: '#contact' },
  ];

  const navItems = isMainPage ? mainPageNavItems : locationPageNavItems;

  // Filter locations for dropdown (exclude Surabaya since it's the main page)
  const otherLocations = locations.filter(loc => loc.slug !== 'surabaya');

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src={logoUrl}
              alt={displayName}
              className="w-12 h-12 rounded-full object-cover"
            />
            <span className={`font-bold text-lg tracking-tight hidden sm:block ${
              isScrolled ? 'text-[#8B1C1C]' : 'text-white'
            }`}>
              {displayName}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`text-sm font-medium transition-colors hover:text-[#8B1C1C] ${
                  isScrolled ? 'text-[#1e1919]' : 'text-white'
                }`}
              >
                {t(item.labelEn, item.labelId)}
              </a>
            ))}
            
            {/* Locations Dropdown - show other locations */}
            {otherLocations.length > 0 && (
              <div className="relative" ref={locationsRef}>
                <button
                  onClick={() => setIsLocationsOpen(!isLocationsOpen)}
                  className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-[#8B1C1C] ${
                    isScrolled ? 'text-[#1e1919]' : 'text-white'
                  }`}
                >
                  {t('Locations', 'Standorte')}
                  <ChevronDown className={`w-4 h-4 transition-transform ${isLocationsOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isLocationsOpen && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                    <div className="p-2">
                      {/* Main Surabaya link */}
                      <Link
                        to="/"
                        onClick={() => setIsLocationsOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[#f7f5f2] transition-colors ${
                          isMainPage ? 'bg-[#8B1C1C]/10 text-[#8B1C1C]' : 'text-[#1e1919]'
                        }`}
                      >
                        <MapPin className="w-4 h-4 text-[#8B1C1C]" />
                        <div>
                          <p className="font-semibold">Surabaya</p>
                          <p className="text-xs text-[#736c64]">Indonesia</p>
                        </div>
                      </Link>
                      
                      {/* Other locations */}
                      {otherLocations.map((loc) => (
                        <Link
                          key={loc.id}
                          to={`/standorte/${loc.slug}`}
                          onClick={() => setIsLocationsOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[#f7f5f2] transition-colors ${
                            currentLocation?.id === loc.id ? 'bg-[#8B1C1C]/10 text-[#8B1C1C]' : 'text-[#1e1919]'
                          }`}
                        >
                          <MapPin className="w-4 h-4 text-[#8B1C1C]" />
                          <div>
                            <p className="font-semibold">{loc.city}</p>
                            <p className="text-xs text-[#736c64]">{loc.country}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                isScrolled ? 'text-[#1e1919]' : 'text-white'
              } hover:text-[#8B1C1C]`}
            >
              <Globe className="w-4 h-4" />
              {language === 'en' ? 'ID' : 'EN'}
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${
              isScrolled ? 'text-[#1e1919]' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t shadow-lg">
          <div className="px-4 py-6 space-y-4">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-[#1e1919] font-medium py-2 hover:text-[#8B1C1C] transition-colors"
              >
                {t(item.labelEn, item.labelId)}
              </a>
            ))}
            
            {/* Locations in Mobile */}
            <div className="border-t pt-4">
              <p className="text-[#736c64] text-sm font-semibold mb-3">{t('Locations', 'Standorte')}</p>
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 py-2 hover:text-[#8B1C1C] transition-colors ${
                  isMainPage ? 'text-[#8B1C1C]' : 'text-[#1e1919]'
                }`}
              >
                <MapPin className="w-4 h-4" />
                <span>Surabaya, Indonesia</span>
              </Link>
              {otherLocations.map((loc) => (
                <Link
                  key={loc.id}
                  to={`/standorte/${loc.slug}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 py-2 hover:text-[#8B1C1C] transition-colors ${
                    currentLocation?.id === loc.id ? 'text-[#8B1C1C]' : 'text-[#1e1919]'
                  }`}
                >
                  <MapPin className="w-4 h-4" />
                  <span>{loc.city}, {loc.country}</span>
                </Link>
              ))}
            </div>
            
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 text-[#1e1919] font-medium py-2 hover:text-[#8B1C1C] transition-colors"
            >
              <Globe className="w-4 h-4" />
              {language === 'en' ? 'Bahasa Indonesia' : 'English'}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
